<script setup>
import { computed, inject } from 'vue';
import FeatureIcon from '../FeatureIcon.vue';
import { featureItems } from '../blockContent.js';
import { styleVars } from './shared.js';

const props = defineProps({
    config: { type: Object, required: true },
    accent: { type: String, default: '' },
    org: { type: Object, default: () => ({}) },
    site_data: { type: Object, default: () => ({}) },
});

// No features is no block for a visitor, heading included, and a feature
// with nothing in it is left out; the editor keeps the empty block and
// its prompt (Mus, 2026-10-11).
const editing = inject('tiqEditorCanvas', false);
const features = computed(() => featureItems(props.config, editing));

// Classic's three button styles, mapped onto Trailhead's buttons.
// 'link' is not a button at all there: it is the underlined "Read
// article →" treatment the rest of the template uses.
function buttonClass(style) {
    if (style === 'ghost') return 'th-btn th-btn--ghost th-btn--sm';
    if (style === 'link') return 'th-fg__link';
    return 'th-btn th-btn--primary th-btn--sm';
}
</script>

<template>
    <section v-if="features.length || editing" :id="config.anchor_id || null" class="th-block th-band th-band--short th-tint th-fg" :style="styleVars(config)">
        <div class="th-wrap">
            <div v-if="config.eyebrow || config.heading || config.body" class="th-fg__head">
                <div>
                    <p v-if="config.eyebrow" class="th-eyebrow">{{ config.eyebrow }}</p>
                    <h2 v-if="config.heading" class="th-h2">{{ config.heading }}</h2>
                </div>
                <p v-if="config.body" class="th-lede">{{ config.body }}</p>
            </div>

            <div v-if="features.length" class="th-grid th-fg__grid">
                <div v-for="(f, i) in features" :key="i" class="th-card th-fg__card">
                    <div class="th-fg__icon">
                        <FeatureIcon :name="f.icon || 'star'" />
                    </div>
                    <h3 v-if="f.title" class="th-h3 th-fg__title">{{ f.title }}</h3>
                    <p v-if="f.desc" class="th-small">{{ f.desc }}</p>
                    <a v-if="f.button?.label"
                       :href="f.button.href || '#'"
                       class="th-fg__cta"
                       :class="buttonClass(f.button.style)">
                        {{ f.button.label }}<template v-if="(f.button.style || 'primary') === 'link'"> →</template>
                    </a>
                </div>
            </div>
            <p v-else-if="editing" class="th-small th-fg__empty">Add features in the editor.</p>
        </div>
    </section>
</template>

<style scoped>
.th-fg__head {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(min(100%, 340px), 1fr));
    gap: clamp(20px, 3vw, 48px);
    align-items: end;
    margin-bottom: clamp(32px, 5vw, 56px);
}
.th-fg__grid { --th-min: 280px; --th-gap: clamp(12px, 2vw, 20px); }
.th-fg__card { display: flex; flex-direction: column; gap: 12px; padding: clamp(20px, 2.5vw, 28px); }
.th-fg__icon { width: 34px; height: 34px; color: var(--accent); }
.th-fg__title { font-size: 20px; }
.th-fg__cta { margin-top: auto; align-self: flex-start; }
.th-fg__link {
    font-weight: 600;
    font-size: 15px;
    border-bottom: 2px solid var(--accent);
    text-decoration: none !important;
}
.th-fg__empty { font-style: italic; }
</style>
